import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Building2, CircleAlert, Phone, Send } from "lucide-react";
import { getInsuranceByWildcard, InsuranceAvatar } from "@ascleo/insurances-client";
import { useCheckInSend, useCheckInContext } from "../context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { canRenderKimAddressQrCode, KimAddressQrCode } from "./KimAddressQrCode";
import { useKimAddress } from "../../../../../lib/use-kim-address";
import { FEATURE_FLAGS } from "../../../../../platform/defaults";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { useAudio } from "../../../../../lib/use-audio";
import { AltCard } from "../../../components/AltCard";
import { KioskStep, KioskStepColumns } from "../../../../../components/kiosk-step";

const COUNTDOWN_SECONDS = 60;

/**
 * Insurer Contact Step: after the insurer is known, recommends one way to get
 * the proof (the insurance app) and lists the alternatives (electronic request,
 * calling the insurer) below it. "Erledigt – weiter" is the footer action and
 * shows the remaining waiting time in its label (KC-43); the reception is the
 * secondary action. The practice's KIM address is never part of the patient
 * instructions (KC-04), and the step scrolls with the page (KC-15).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <InsurerContactStep />
 * ```
 */
export function InsurerContactStep() {
  const { t } = useTranslation();
  useAudio("checkIn-insurerContact");
  const send = useCheckInSend();
  const context = useCheckInContext();
  const [insurerPhone, setInsurerPhone] = useState<string | null>(null);
  const [isLoadingPhone, setIsLoadingPhone] = useState(true);
  const [phoneLoadFailed, setPhoneLoadFailed] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const { kimInfo } = useKimAddress();

  useEffect(function countDownBeforeContinue() {
    const timer = setInterval(() => {
      setCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    return function stopContinueCountdown() {
      clearInterval(timer);
    };
  }, []);

  const insurerIknr = context.selectedIknr || context.previousInsurerIknr;
  const insurerName = context.selectedInsurerName || context.previousInsurerName;

  useEffect(
    function runLoadInsurerPhoneEffect() {
      let active = true;
      const loadInsurerPhone = async () => {
        if (!insurerIknr) {
          setIsLoadingPhone(false);
          return;
        }
        try {
          const insurer = await docCirrusRpcClient.frontdesk.getInsurerByIk({
            iknr: insurerIknr,
          });
          if (active && insurer?.phone) setInsurerPhone(insurer.phone);
        } catch (err) {
          frontdeskDiagnosticLogger.error("Failed to load insurer contact details", {
            error: err,
            hasInsurerIknr: Boolean(insurerIknr),
          });
          if (active) setPhoneLoadFailed(true);
        } finally {
          if (active) setIsLoadingPhone(false);
        }
      };
      void loadInsurerPhone();
      return function ignoreStaleInsurerPhone() {
        active = false;
      };
    },
    [insurerIknr],
  );

  const insuranceIdentity = useMemo(() => {
    if (!insurerName) return null;
    return getInsuranceByWildcard(insurerName)[0] ?? null;
  }, [insurerName]);

  const eebEnabled = FEATURE_FLAGS.eeb && kimInfo !== null;
  const qrAvailable = kimInfo !== null && canRenderKimAddressQrCode(kimInfo.kimAddress);
  const countdownComplete = countdown === 0;

  const phoneLine = insurerPhone
    ? insurerPhone
    : !isLoadingPhone
      ? t("self-service.check-in.insurer-contact.phone-not-available")
      : undefined;

  const alternatives = (
    <section className="flex min-w-0 flex-col gap-3">
      <h2 className="m-0 text-lg font-semibold">
        {t("self-service.check-in.card-check.other-ways-title")}
      </h2>
      {eebEnabled ? (
        <AltCard
          icon={<Send />}
          iconTone="primary"
          className="min-h-20"
          title={t("self-service.check-in.insurer-contact.option-eeb-title")}
          desc={t("self-service.check-in.insurer-contact.option-eeb-description-v2")}
          data-testid="frontdesk-insurer-contact-submit-eeb-btn"
          onClick={() => send({ type: "SUBMIT_EEB_REQUEST" })}
        />
      ) : null}
      <div
        data-testid="frontdesk-insurer-contact-call"
        className="flex min-w-0 flex-col gap-3 rounded-2xl border bg-card p-5"
      >
        <p className="m-0 flex items-center gap-3 text-lg font-semibold">
          <Phone aria-hidden className="size-6 shrink-0 text-primary" />
          {t("self-service.check-in.insurer-contact.option-call-title")}
        </p>
        <p className="m-0 text-lg text-foreground">
          {kimInfo?.practiceName
            ? t("self-service.check-in.insurer-contact.call-instruction-practice", {
                practice: kimInfo.practiceName,
              })
            : t("self-service.check-in.insurer-contact.call-instruction")}
        </p>
        {phoneLine ? (
          <p
            data-testid={phoneLoadFailed ? "frontdesk-insurer-contact-phone-error" : undefined}
            className="m-0 flex items-center gap-2 text-xl font-semibold break-words"
          >
            {phoneLoadFailed ? (
              <CircleAlert aria-hidden className="size-5 shrink-0 text-warning-ink" />
            ) : null}
            {phoneLine}
          </p>
        ) : null}
      </div>
    </section>
  );

  return (
    <KioskStep
      width={qrAvailable ? "wide" : "narrow"}
      title={t("self-service.check-in.insurer-contact.title-v2")}
      lead={
        qrAvailable
          ? t("self-service.check-in.insurer-contact.subtitle-v2")
          : t("self-service.check-in.insurer-contact.subtitle-no-app")
      }
      primaryAction={{
        label: countdownComplete
          ? t("self-service.check-in.insurer-contact.done-button")
          : t("self-service.check-in.insurer-contact.done-button-countdown", {
              seconds: countdown,
            }),
        icon: ArrowRight,
        onClick: () => send({ type: "INSURER_CONTACTED" }),
        disabled: !countdownComplete,
        testId: "frontdesk-insurer-contact-contacted-btn",
      }}
      secondaryAction={{
        label: t("self-service.check-in.eeb-request.at-reception"),
        icon: Building2,
        onClick: () => send({ type: "INSURER_CONTACTED" }),
        testId: "insurer-contact-go-to-counter",
      }}
    >
      <div className="flex min-w-0 items-center gap-3 self-start rounded-full border bg-card px-5 py-3">
        {insuranceIdentity ? (
          <InsuranceAvatar identity={insuranceIdentity} size="sm" showBackground={true} />
        ) : (
          <Building2 aria-hidden className="size-6 text-primary" />
        )}
        <span className="text-lg font-semibold break-words">{insurerName}</span>
      </div>

      {qrAvailable && kimInfo ? (
        <KioskStepColumns aside={alternatives}>
          <KimAddressQrCode
            kimAddress={kimInfo.kimAddress}
            practiceName={kimInfo.practiceName}
            size={200}
          />
        </KioskStepColumns>
      ) : (
        alternatives
      )}
    </KioskStep>
  );
}
